import {
    Accordion,
    Avatar,
    Blockquote,
    Button,
    Card,
    Container,
    Group,
    Image,
    List,
    rem,
    Text,
    ThemeIcon,
    Title,
} from "@mantine/core";
import { IconCheck, IconGift } from "@tabler/icons-react";
import React from "react";
import BackButton from "./BackButton";
import { getUser, handleOnCheckout } from "./Authentication";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import Testimonials from "./Testimonials";
import { useMediaQuery } from "@mantine/hooks";
import { FaChess, FaHeadSideVirus, FaPuzzlePiece, FaQuoteRight } from "react-icons/fa6";

interface PremiumCardProps {
    title: string;
    description: string;
    monthlyPrice: string;
    onClick: () => void;
}

const PremiumCard: React.FC<PremiumCardProps> = ({ title, description, monthlyPrice, onClick }) => {
    return (
        <Card shadow="sm" padding="lg" radius="md" withBorder className="w-80">
            <Card.Section>
                {/* <Image
                        src="https://raw.githubusercontent.com/mantinedev/mantine/master/.demo/images/bg-8.png"
                        height={160}
                        alt="Norway"
                    /> */}
            </Card.Section>
            <Group justify="space-between" mt="md" mb="xs">
                <Text fw={500} fz="xl">
                    {title}
                </Text>
            </Group>
            <div className="mt-2 h-24 text-left">
                <Text size="sm" c="dimmed">
                    {description}
                </Text>
            </div>
            <Text fw="bold" fz="lg">
                ${monthlyPrice}{" "}
                <Text span fz="xs">
                    /month
                </Text>
            </Text>
            <Button onClick={onClick} size="lg" color="blue" fullWidth mt="md" radius="md">
                Get {title}
            </Button>
        </Card>
    );
};

const Premium = () => {
    const navigate = useNavigate();
    const { data, isLoading } = useQuery(["user"], () => getUser());
    const isMobile = useMediaQuery("(max-width: 768px)");

    return (
        <div>
            <BackButton nativeBack={true} />
            <div className="mt-32">
                <Title order={1} my="xl" ta="center">
                    Go Premium and get full access
                </Title>
            </div>
            <div className="flex justify-center flex-col md:flex-row items-center md:space-x-10 space-y-6 md:space-y-0 mb-24">
                <PremiumCard
                    onClick={async () => {
                        if (!data && !isLoading) {
                            navigate("/login");
                        }
                        await handleOnCheckout("prod_RLo6lsLIZj348o");
                    }}
                    title="Monthly"
                    description="Recurring billing. Cancel anytime."
                    monthlyPrice="8.00"
                />
                <PremiumCard
                    onClick={async () => {
                        if (!data && !isLoading) {
                            navigate("/login");
                        }
                        await handleOnCheckout("prod_RLo7MsFvevjZFf");
                    }}
                    title="Yearly"
                    description="Recurring billing. Cancel anytime. Billed as one payment of $80."
                    monthlyPrice="6.66"
                />
            </div>
            <Title order={2} mb="md" ta="center">
                What's included
            </Title>
            <div className="md:ml-[22%]">
                <List
                    // grid-cols-[310px_310px]
                    className="text-left grid grid-cols-1 gap-y-6 lg:gap-x-10 md:gap-x-4"
                    size="xl"
                    p="lg"
                    pt="sm"
                    mb="4rem"
                    center
                    icon={
                        <ThemeIcon color="green" variant="transparent" size={24} radius="xl">
                            <IconCheck style={{ width: rem(16), height: rem(16) }} />
                        </ThemeIcon>
                    }
                >
                    <List.Item className="flex">Custom starting positions</List.Item>
                    <List.Item>
                        <div className="flex items-center space-x-2">
                            <span>Detailed statistics view</span>
                        </div>
                    </List.Item>
                    <List.Item className="flex">"Repeat line" button (learn from your mistakes)</List.Item>
                    {/* <List.Item className="flex">Ranked runs history and stats</List.Item> */}
                    <List.Item className="flex">Access to all engine levels</List.Item>
                    {/* <List.Item className="flex">Configurable settings menu</List.Item> */}
                    <List.Item>
                        <div className="flex items-center space-x-2">
                            <span>Make engine prefer trappy/dubious lines</span>
                        </div>
                    </List.Item>
                    <List.Item>
                        <div className="flex items-center space-x-2">
                            <span>Mainline training: Make engine only pick moves played at least x % of the time</span>
                        </div>
                    </List.Item>
                    {/* <List.Item>
                        <div className="flex items-center space-x-2">
                            <span>Select game duration in rated mode</span>
                        </div>
                    </List.Item> */}
                    <List.Item>
                        <div className="flex items-center space-x-2">
                            <span>Download your played openings in a .pgn file</span>
                        </div>
                    </List.Item>
                    <List.Item>
                        <div className="flex items-center space-x-2">
                            <span>View a list of all opening games which had mistakes</span>
                        </div>
                    </List.Item>
                    <List.Item>
                        <div className="flex items-center space-x-2">
                            <span>"Train weakest lines" mode in each of your openings</span>
                        </div>
                    </List.Item>
                    <List.Item>
                        <div className="flex items-center space-x-2">
                            <span>Train against a specific player</span>
                        </div>
                    </List.Item>
                    <List.Item>
                        <div className="flex items-center space-x-2">
                            <span>Game explorer: Aggregated data of all your OpeningTrainer games</span>
                        </div>
                    </List.Item>
                    <List.Item>
                        <div className="flex items-center space-x-2">
                            <span>Advanced move exlusion: Fine-tune what moves the engine should play</span>
                        </div>
                    </List.Item>

                    <List.Item className="flex">Priority to customer service for new feature additions</List.Item>

                    {/* {!isSmallerScreen && (
                        <>
                            <List.Item>
                                <div className="flex items-center space-x-2">
                                    <span>
                                        Upload your repo and get notified if you make moves inconsistent with it
                                    </span>
                                    <Badge size="sm" color="blue">
                                        Coming soon
                                    </Badge>
                                </div>
                            </List.Item>
                        </>
                    )} */}
                </List>
            </div>

            <div className="w-full flex justify-center mx-4 mt-36">
                <Blockquote
                    maw="690px"
                    radius="md"
                    iconSize={40}
                    styles={{ cite: { opacity: 1 }, root: { backgroundColor: "#25262b" } }}
                    cite={
                        <div className="text-left flex">
                            <Avatar src="/nathan.png" className="pointer-events-none" />
                            <div className="ml-4">
                                <h3 className="h5 font-primary font-semibold not-italic">Nathan</h3>
                                <Text className="text-white not-italic" fz="sm" c="dimmed">
                                    1950 FIDE
                                </Text>
                            </div>
                        </div>
                    }
                    icon={<FaQuoteRight size="1.5rem" />}
                >
                    <Text ta="left">
                        It’s incredible how effective it is to train the same specific opening over and over. Just 30
                        repetitions and I feel like I have a whole new understanding of that opening.
                    </Text>
                </Blockquote>
            </div>

            <div className="grid grid-cols-1 space-y-8 md:space-y-0 md:grid-cols-[6fr_7fr] gap-x-20 lg:my-32 max-w-[1320px] ml-auto mr-auto">
                <Image src="/section3c_no_title.png" h={500} w={689} alt="alapin" style={{ objectFit: "contain" }} />
                <div className="text-left flex justify-center flex-col">
                    <Title order={2} mb="md" ta="center">
                        Why OpeningTrainer
                    </Title>

                    <Text fz="lg" px="md">
                        While other tools focus on plain memorization of moves, OpeningTrainer lets you play actual
                        simulated games in the opening phase. This way of training allows you to:
                    </Text>
                    <List
                        className="text-left grid grid-cols-1 gap-y-6 gap-x-10"
                        size="xl"
                        p="lg"
                        pt="xl"
                        mb="4rem"
                        fz="lg"
                        center
                        icon={
                            <ThemeIcon color="green" variant="transparent" size={24} radius="xl">
                                <IconCheck style={{ width: rem(16), height: rem(16) }} />
                            </ThemeIcon>
                        }
                    >
                        <List.Item>Maximize active learning</List.Item>

                        <List.Item>Simulate specific variations dozens of times in just minutes</List.Item>

                        <List.Item className="flex">
                            Have more fun! No more tedious lines to review over and over.
                        </List.Item>
                    </List>
                </div>
            </div>

            <div className="grid grid-cols-1 space-y-8 md:space-y-0 md:grid-cols-[6fr_7fr] md:my-32 max-w-[1320px] ml-auto mr-auto">
                <div className="text-left flex justify-center flex-col items-center">
                    <div className="md:hidden flex">
                        <Image src="/alapin.png" h={400} w={551} alt="alapin" style={{ objectFit: "contain" }} />
                    </div>
                    <Text c="dimmed" fz="sm" ta="center" hiddenFrom="sm" mt="xs">
                        Actual improvement chart of one of our users
                    </Text>

                    <List
                        className="text-left grid grid-cols-1 gap-y-6 gap-x-10"
                        size="xl"
                        p="lg"
                        pt={isMobile ? "6rem" : "xl"}
                        mb="4rem"
                        fz="lg"
                        center
                        icon={
                            <ThemeIcon color="green" variant="transparent" size={24} radius="xl">
                                <IconCheck style={{ width: rem(16), height: rem(16) }} />
                            </ThemeIcon>
                        }
                    >
                        <List.Item>Get a unique rating in each of your favorite openings</List.Item>

                        <List.Item>Track your progress with detailed statistics</List.Item>

                        <List.Item className="flex">Break your plateue!</List.Item>
                    </List>
                </div>
                <div className="max-md:hidden mr-8">
                    <div>
                        <Image src="/alapin.png" h={500} w={689} alt="alapin" style={{ objectFit: "contain" }} />
                    </div>
                    <Text c="dimmed" fz="sm" mt="xs">
                        Actual improvement chart of one of our users
                    </Text>
                </div>
            </div>

            <div className="p-8 border-[1px] border-[#1864ab] rounded-lg max-w-[690px] mx-auto max-md:mx-2 mb-32 mt-32 flex flex-col items-center">
                <Group gap="xs" mb="lg">
                    <IconGift size="3rem" />
                    <Text fz="h2">Bonus</Text>
                </Group>
                <Text>
                    Full access to{" "}
                    <a href="https://endgametrainer.com" className="text-inherit">
                        endgametrainer.com
                    </a>{" "}
                    included
                </Text>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
                    <div className="flex flex-col items-center">
                        <FaPuzzlePiece size="1.8rem" />
                        <Text className="opacity-70" fz="sm" mt="8px">
                            6000+ high-quality
                            <br />
                            endgame puzzles
                        </Text>
                    </div>
                    <div className="flex flex-col items-center">
                        <FaChess size="1.8rem" />
                        <Text className="opacity-70" fz="sm" mt="8px">
                            27 Puzzle <br /> categories
                        </Text>
                    </div>
                    <div className="flex flex-col items-center">
                        <FaHeadSideVirus size="1.8rem" />
                        <Text className="opacity-70" fz="sm" mt="8px">
                            Smart <br /> human-like AI
                        </Text>
                    </div>
                </div>
            </div>

            <Title order={2} mb="xl" ta="center">
                Loved by 5000+ users
            </Title>

            <Testimonials />

            <div className="mt-16 mx-1">
                <Text c="dimmed">Still on the fence? No sweat. You’ve got a 14-day money back guarantee.</Text>
            </div>

            <Container size="sm" className="my-24">
                <Title ta="left" order={2} mb="lg">
                    Frequently Asked Questions
                </Title>

                <Accordion variant="separated" ta="left" maw="40rem" className="leading-8">
                    <Accordion.Item value="question6">
                        <Accordion.Control>How many openings do you support?</Accordion.Control>
                        <Accordion.Panel>
                            <p>
                                There are no limits to to what openings you can train. You will be able to set up any
                                starting position and add it to your list of openings. Then you can pick positions from
                                your list and train from there. Each position will get a unique rating and statistics.
                            </p>
                        </Accordion.Panel>
                    </Accordion.Item>
                    <Accordion.Item value="question5">
                        <Accordion.Control>How do I cancel my subscription</Accordion.Control>
                        <Accordion.Panel>
                            <p>
                                You may cancel your subscription at any time in your payment settings. When you cancel,
                                your subscription ends at the end of the current billing cycle.
                            </p>
                            <br />
                            <p>The payment settings can be found in the "Manage subscription" menu option</p>
                        </Accordion.Panel>
                    </Accordion.Item>
                    <Accordion.Item value="question0">
                        <Accordion.Control>How does the money back guarantee work?</Accordion.Control>
                        <Accordion.Panel>
                            We provide a 14-day money back guarantee which means that if you apply for a refund within
                            14 days of your purchase, you will receive every cent of your money back (no questions
                            asked). You can apply for a refund by sending an email to mail@openingtrainer.com.
                        </Accordion.Panel>
                    </Accordion.Item>

                    <Accordion.Item value="question1">
                        <Accordion.Control>
                            If I buy a Yearly membership, is it billed all at once or monthly?
                        </Accordion.Control>
                        <Accordion.Panel>
                            Yearly subscriptions are billed in one payment every year, while monthly memberships recur
                            automatically each month.
                        </Accordion.Panel>
                    </Accordion.Item>

                    <Accordion.Item value="question3">
                        <Accordion.Control>
                            There is a problem with my payment / Premium membership - who can I contact?
                        </Accordion.Control>
                        <Accordion.Panel>
                            You can contact us at any time by writing to mail@openingtrainer.com. We'll be happy to
                            help!
                        </Accordion.Panel>
                    </Accordion.Item>
                    <Accordion.Item value="question7">
                        <Accordion.Control>I have more questions</Accordion.Control>
                        <Accordion.Panel>
                            Feel free to join our lovely <a href="https://discord.gg/WNytKVEa8S">Discord community</a>{" "}
                            and ask your questions there. There you will find people who are happy to help. You can also
                            send contact us by sending an email to mail@openingtrainer.com
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            </Container>
        </div>
    );
};

export default Premium;
